import React, { useMemo, useState } from "react";
import { isEmpty } from "lodash";
import { Container } from "react-bootstrap";

import { Breadcrumbs, Button } from "components";
import {
	LABELS_COMPARISON_MERCHANT_RESULT,
	LABELS_COMPARISON_INQUIRY_MATCH_RESULT,
} from "mappers";
import {
	useMerchantMatchDetailsMutation,
	useInquiryMatchDetailsMutation,
} from "queries";

import ComparisonPage from "./comparisonPage";
import MatchReport from "./matchReport";
import UnderwritingBreadcrumbs from "../underwritingBreadcrumbs";

import { ReactComponent as ArrowLeftIcon } from "static/images/arrow_left.svg";
import { ReactComponent as ArrowRightIcon } from "static/images/arrow_right.svg";
import styles from "./matchReport.module.scss";

type MatchReportPageProps = {
	isComparable: boolean;
	match: {
		isExact?: boolean;
		path: string;
		params: {
			tab?: string;
			id: string;
			underwritingTab?: string;
			applicationId: string;
		};
		url?: string;
	};
};

const MatchReportPage = ({ match }: MatchReportPageProps) => {
	const [isComparable, setIsComparable] = useState<boolean>(false);
	const [isShowMerchantMatchComparison, setIsShowMerchantMatchComparison] =
		useState<boolean>(false);
	const [isShowInquiryMatchComparison, setIsShowInquiryMatchComparison] =
		useState<boolean>(false);
	const [merchantMatchDetails, setMerchantMatchDetails] =
		useState<Object | null>(null);
	const [inquiryMatchDetails, setInquiryMatchDetails] =
		useState<Object | null>(null);

	const isMerchantPage = useMemo(
		() => match?.url?.startsWith("/merchant/"),
		[match?.url],
	);

	const applicationId: string | number | undefined = isMerchantPage
		? match?.params?.applicationId
		: match?.params?.id;

	const {
		mutate: getMerchantMatchDetails,
		isLoading: isLoadingMerchantMatchDetails,
	} = useMerchantMatchDetailsMutation({ setMerchantMatchDetails });

	const {
		mutate: getInquiryMatchDetails,
		isLoading: isLoadingInquiryMatchDetails,
	} = useInquiryMatchDetailsMutation({ setInquiryMatchDetails });

	const handleViewClick = (
		selectedMerchantMatches,
		selectedInquiryMatches,
	) => {
		setIsComparable(true);
		if (selectedMerchantMatches.length > 0) {
			getMerchantMatchDetails({ applicationId, selectedMerchantMatches });
			setIsShowMerchantMatchComparison(!isShowMerchantMatchComparison);
		}
		if (selectedInquiryMatches.length > 0) {
			getInquiryMatchDetails({
				applicationId,
				selectedInquiryMatches,
			});
			setIsShowInquiryMatchComparison(!isShowInquiryMatchComparison);
		}
	};

	const backOnMatchReport = () => {
		setIsComparable(false);
		setMerchantMatchDetails(null);
		setInquiryMatchDetails(null);
		setIsShowMerchantMatchComparison(false);
		setIsShowInquiryMatchComparison(false);
	};

	const MerchantBreadcrumbs = () => {
		return (
			<Breadcrumbs
				pages={[
					{
						title: "Merchant List",
						link: "/merchant/list",
					},
					{
						title: "Merchant Info",
						link: `/merchant/list/info/${match.params.id}/general`,
					},
					{
						title: "U / W Results",
						link: `/merchant/list/info/${match.params.id}/results`,
					},
					{
						title: "MATCH Report",
					},
				]}
			/>
		);
	};

	const isLoading =
		isLoadingMerchantMatchDetails || isLoadingInquiryMatchDetails;

	const isDisabledInquiryMatchBtn = isEmpty(merchantMatchDetails);
	const isDisabledMerchantMatchBtn = isEmpty(inquiryMatchDetails);

	const handlePossibleMatch = (isShowMerchantMatch, isShowInquiryMatch) => {
		setIsShowMerchantMatchComparison(isShowMerchantMatch);
		setIsShowInquiryMatchComparison(isShowInquiryMatch);
	};

	return (
		<div className="page-content-wrapper">
			{isMerchantPage ? (
				<MerchantBreadcrumbs />
			) : (
				<UnderwritingBreadcrumbs
					match={match}
					isComparable={isComparable}
					clickOnMatchReport={backOnMatchReport}
				/>
			)}
			<Container fluid className={styles.contentWrapper}>
				{isComparable ? (
					<>
						{isShowMerchantMatchComparison && (
							<ComparisonPage
								loading={isLoading}
								matchDetails={merchantMatchDetails || {}}
								titleColumn="Merchant MATCH Results"
								comparisonLabels={
									LABELS_COMPARISON_MERCHANT_RESULT
								}
								PossibleButton={() => (
									<Button
										minWidth="300px"
										variant="secondary"
										value="go to possible inquiry matches"
										iconPosition="right"
										className="pl-0 d-flex align-items-center"
										skeletonWidth="250px"
										onClick={() => {
											handlePossibleMatch(false, true);
										}}
										isLoading={isLoading}
										disabled={
											isDisabledMerchantMatchBtn ||
											isLoading
										}
										icon={
											<ArrowRightIcon
												className={styles.iconLink}
												fill={
													isDisabledMerchantMatchBtn
														? "#ffffff14"
														: "#13ACC5"
												}
											/>
										}
									/>
								)}
							/>
						)}
						{isShowInquiryMatchComparison &&
							!isShowMerchantMatchComparison && (
								<ComparisonPage
									loading={isLoading}
									matchDetails={inquiryMatchDetails || {}}
									titleColumn="Inquiry MATCH Results"
									comparisonLabels={
										LABELS_COMPARISON_INQUIRY_MATCH_RESULT
									}
									PossibleButton={() => (
										<Button
											minWidth="300px"
											variant="secondary"
											value="back to possible merchant matches"
											iconPosition="left"
											className="pl-0 d-flex align-items-center"
											skeletonWidth="250px"
											onClick={() => {
												handlePossibleMatch(
													true,
													false,
												);
											}}
											isLoading={isLoading}
											disabled={
												isDisabledInquiryMatchBtn ||
												isLoading
											}
											icon={
												<ArrowLeftIcon
													className={styles.iconLink}
													fill={
														isDisabledInquiryMatchBtn
															? "#ffffff14"
															: "#13ACC5"
													}
												/>
											}
										/>
									)}
								/>
							)}
					</>
				) : (
					<MatchReport
						handleViewClick={handleViewClick}
						appId={applicationId}
					/>
				)}
			</Container>
		</div>
	);
};

export default MatchReportPage;
